import { Link } from "@tanstack/react-router";
import { Img } from "@/components/ui/img";
import { cn } from "@/lib/utils";
import { formatDateWithOptionsBs } from "@/lib/format";
import { PAGE_STATUS_CONFIG } from "@/lib/status-configs";
import { Pencil, Trash, CalendarDots, ArrowSquareOut, Translate, Plus } from "@phosphor-icons/react";
import type { Page } from "@/types";
import { LOCALE_MAP } from "@/lib/locale";

function formatPageDate(iso: string): string {
  return formatDateWithOptionsBs(iso, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Sarajevo",
  });
}

interface PageWithTranslations extends Page {
  translations?: { id: string; locale: string; status: string }[];
}

export function PageCard({
  page,
  onDelete,
}: {
  page: PageWithTranslations;
  onDelete: () => void;
}) {
  const config = PAGE_STATUS_CONFIG[page.status];
  const localeInfo = LOCALE_MAP[page.locale];
  const missingLocales = ["bs", "en"].filter(
    (l) => l !== page.locale && !page.translations?.some((t) => t.locale === l)
  );

  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-all duration-200 hover:shadow-md sm:flex-row">
      {page.coverImageUrl && (
        <div className="relative h-36 shrink-0 overflow-hidden sm:h-auto sm:w-44">
          <Img
            src={page.coverImageUrl}
            alt=""
            className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      )}

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3
                className="line-clamp-1 text-base font-semibold text-foreground sm:text-lg"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {page.title}
              </h3>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {localeInfo?.flag} {localeInfo?.short}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <CalendarDots className="size-3.5" />
                {formatPageDate(page.updatedAt || page.createdAt)}
              </span>
              <span className="font-mono text-muted-foreground/60">
                /{page.slug}
              </span>
              {page.translations && page.translations.length > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Translate className="size-3.5" />
                  {page.translations.map((t) => LOCALE_MAP[t.locale]?.flag || t.locale).join(" ")}
                </span>
              )}
            </div>
          </div>
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1",
              config.bg,
              config.color,
              config.ring
            )}
          >
            <span className={cn("size-1.5 rounded-full", config.dot)} />
            {config.label}
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 pt-3">
          <div className="flex gap-1">
            {missingLocales.map((locale) => (
              <Link
                key={locale}
                to="/dashboard/admin/pages/new"
                search={{ groupId: page.groupId, locale }}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
              >
                <Plus className="size-3" />
                {LOCALE_MAP[locale]?.flag} {LOCALE_MAP[locale]?.label}
              </Link>
            ))}
          </div>

          <div className="flex gap-1">
            {page.status === "published" && (
              <a
                href={`/pages/${page.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Pogledaj"
              >
                <ArrowSquareOut className="size-4" />
              </a>
            )}
            <Link
              to={`/dashboard/admin/pages/${page.id}/edit`}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Uredi"
            >
              <Pencil className="size-4" />
            </Link>
            <button
              onClick={onDelete}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600"
              aria-label="Obriši"
            >
              <Trash className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
